import { useState } from 'react';

import { Control, FieldValues, Path } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { getPhoneValidationForCountry, PHONE_FORMATS } from '@/modules/common/constants';

interface PhoneNumberInputProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Comprehensive international phone number input component.
 * Supports 150+ countries with country-specific formatting and validation.
 *
 * Features:
 * - Comprehensive international phone number formatting for 150+ countries
 * - Real-time country detection and format application
 * - Country-specific validation with proper digit length checking
 * - Dynamic maxLength adjustment based on detected country
 * - Automatic formatting as user types (e.g., +1 (555) 123-4567)
 * - Support for all major regions: North America, Europe, Asia-Pacific,
 *   Middle East, Africa, and Americas
 * - React Hook Form integration with validation
 * - Fallback formatting and validation for unknown country codes
 * - ITU-T E.164 standard compliance
 *
 * Supported regions include:
 * - North America (NANP): US, Canada
 * - Europe: UK, Germany, France, Italy, Spain, Netherlands, etc.
 * - Asia-Pacific: China, Japan, India, Australia, Singapore, etc.
 * - Middle East: UAE, Saudi Arabia, Israel, etc.
 * - Africa: South Africa, Nigeria, Kenya, etc.
 * - Americas: Mexico, Brazil, Argentina, etc.
 * - And many more...
 *
 * @example
 * ```tsx
 * <PhoneNumberInput
 *   name="phoneNumber"
 *   control={form.control}
 *   label="Phone Number"
 *   placeholder="+1 (555) 123-4567"
 * />
 * ```
 *
 * Examples of formatted output:
 * - US: +1 (555) 123-4567
 * - UK: +44 1234 567 890
 * - Germany: +49 1234 5678901
 * - France: +33 1 23 45 67 89
 * - Japan: +81 90-1234-5678
 * - Australia: +61 412 345 678
 */
// Common utilities
const cleanPhoneInput = (value: string): string => value.replace(/[^\d+]/g, '');

const getSortedCountryCodes = (): string[] =>
  Object.keys(PHONE_FORMATS).sort((a, b) => b.length - a.length);

const findMatchingCountryCode = (digits: string): string | null => {
  const sortedCodes = getSortedCountryCodes();

  for (const countryCode of sortedCodes) {
    const code = countryCode.slice(1); // Remove + from pattern key
    if (digits.startsWith(code)) {
      return countryCode;
    }
  }

  return null;
};

const getMinCursorPosition = (value: string): number => {
  return value.indexOf(' ') > 0 ? value.indexOf(' ') : 2;
};

export function PhoneNumberInput<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  disabled = false,
  className,
}: PhoneNumberInputProps<T>) {
  const [inputValue, setInputValue] = useState('');

  /**
   * Formats phone number as user types for better UX.
   * Uses country-specific formatting patterns for optimal display.
   */
  const formatPhoneNumber = (value: string): string => {
    let cleaned = cleanPhoneInput(value);

    // Ensure it starts with +
    if (cleaned.length > 0 && !cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }

    if (cleaned === '+' || cleaned === '') {
      return cleaned;
    }

    const digits = cleaned.slice(1);
    const countryCode = findMatchingCountryCode(digits);

    // Apply country-specific formatting if found
    if (countryCode) {
      const code = countryCode.slice(1);

      let formatted = PHONE_FORMATS[countryCode].pattern;
      let digitIndex = code.length;

      // Replace X's with actual digits
      for (let i = 0; i < formatted.length && digitIndex < digits.length; i++) {
        if (formatted[i] === 'X') {
          formatted = formatted.substring(0, i) + digits[digitIndex] + formatted.substring(i + 1);
          digitIndex++;
        }
      }

      // Remove remaining X's and append extra digits if any
      formatted = formatted.replace(/X+.*$/, '');
      if (digitIndex < digits.length) {
        const remainingDigits = digits.slice(digitIndex);
        formatted += (formatted.endsWith(' ') ? '' : ' ') + remainingDigits;
      }

      return formatted;
    }

    // Fallback formatting for unknown country codes
    return formatUnknownCountryCode(digits);
  };

  const formatUnknownCountryCode = (digits: string): string => {
    if (digits.length <= 3) return '+' + digits;
    if (digits.length <= 6) return `+${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10)
      return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)} ${digits.slice(10)}`;
  };

  /**
   * Gets the appropriate max length for the current phone number based on detected country.
   */
  const getMaxLength = (value: string): number => {
    if (!value || !value.startsWith('+')) return 25;

    const digits = cleanPhoneInput(value).slice(1);
    const countryCode = findMatchingCountryCode(digits);

    return countryCode ? PHONE_FORMATS[countryCode].maxLength : 25;
  };

  /**
   * Prevents cursor positioning at the beginning of the input.
   */
  const preventCursorAtStart = (input: HTMLInputElement, value: string) => {
    const cursorPosition = input.selectionStart;
    if (cursorPosition !== null && cursorPosition <= 1) {
      const minPosition = getMinCursorPosition(value);
      input.setSelectionRange(minPosition, minPosition);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const value = input.value;

    if (value.startsWith('+')) {
      setTimeout(() => preventCursorAtStart(input, value), 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const value = input.value;
    const cursorPosition = input.selectionStart;

    if (value.startsWith('+') && cursorPosition !== null) {
      if ((e.key === 'ArrowLeft' && cursorPosition <= 2) || e.key === 'Home') {
        e.preventDefault();
        const minPosition = getMinCursorPosition(value);
        input.setSelectionRange(minPosition, minPosition);
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const rawValue = e.target.value;

    // If user is deleting or the value is empty, allow it
    if (rawValue.length < inputValue.length || rawValue === '') {
      setInputValue(rawValue);
      onChange(rawValue);
      return;
    }

    // If user types without +, prepend it
    let processedValue = rawValue;
    if (processedValue.length > 0 && !processedValue.startsWith('+')) {
      processedValue = '+' + processedValue;
    }

    // Format the input
    const formatted = formatPhoneNumber(processedValue);
    setInputValue(formatted);
    onChange(formatted);
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        // Initialize input value from field value
        if (field.value && !inputValue) {
          setInputValue(field.value);
        }

        return (
          <FormItem className={className}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <Input
                {...field}
                type="tel"
                placeholder={placeholder}
                disabled={disabled}
                value={inputValue || field.value || ''}
                onChange={e => handleChange(e, field.onChange)}
                onBlur={field.onBlur}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                className={inputValue || field.value ? 'font-normal' : undefined}
                maxLength={getMaxLength(inputValue || field.value || '')}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

/**
 * Utility function to clean phone number for storage.
 * Removes all formatting, keeping only digits and optional + prefix.
 */
export const cleanPhoneNumber = (phone: string): string => {
  return cleanPhoneInput(phone);
};

/**
 * Core validation logic used by both UI and Zod validation.
 */
const validatePhoneCore = (phone: string): boolean => {
  if (!phone) return false;

  const cleaned = cleanPhoneNumber(phone);
  if (!cleaned.startsWith('+')) return false;

  const digits = cleaned.slice(1);
  if (digits.length === 0) return false;

  const countryCode = findMatchingCountryCode(digits);
  if (countryCode) {
    const validation = getPhoneValidationForCountry(countryCode);
    if (validation) {
      const code = countryCode.slice(1);
      const nationalDigits = digits.slice(code.length);
      return (
        nationalDigits.length >= validation.minDigits &&
        nationalDigits.length <= validation.maxDigits
      );
    }
  }

  // ITU-T E.164 standard fallback: 7-15 digits total
  return digits.length >= 7 && digits.length <= 15;
};

/**
 * Utility function to validate phone number using comprehensive country-specific rules.
 * Supports all major international formats with proper validation.
 * Note: This is for utility validation where empty values are considered invalid.
 * For optional form fields, check for empty values separately.
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  return validatePhoneCore(phone);
};

/**
 * Creates phone number validation function for Zod schema with i18n support.
 * Returns translation keys that FormMessage will handle.
 */
export const createPhoneValidationForZod = () => {
  return (value: string): string | undefined => {
    if (!value) return undefined;

    const cleaned = cleanPhoneNumber(value);

    if (!cleaned.startsWith('+')) {
      return 'common:error.phone.mustStartWithPlus';
    }

    const digits = cleaned.slice(1);
    if (digits.length === 0) {
      return 'common:error.phone.mustIncludeCountryCode';
    }

    const countryCode = findMatchingCountryCode(digits);
    if (countryCode) {
      const validation = getPhoneValidationForCountry(countryCode);
      if (validation) {
        const code = countryCode.slice(1);
        const nationalDigits = digits.slice(code.length);

        if (nationalDigits.length < validation.minDigits) {
          return 'common:error.phone.tooFewDigits';
        }
        if (nationalDigits.length > validation.maxDigits) {
          return 'common:error.phone.tooManyDigits';
        }
      }
      return undefined; // Valid
    }

    // Check if it's a partial country code
    const sortedCodes = getSortedCountryCodes();
    for (const code of sortedCodes) {
      if (code.slice(1).startsWith(digits)) {
        return 'common:error.phone.continueTyping';
      }
    }

    return 'common:error.phone.unknownCountryCode';
  };
};
